import cx from 'classnames'
import {
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FrameBuilderSettingsContext } from '@frame-builder/contexts/settings-context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'
import { FrameBuilderValidationContext } from '@frame-builder/contexts/validation-context'
import {
  defaultStepStates,
  getAnimationFinishedStepStates,
} from '@frame-builder/lib/steps'
import {
  FrameBuilderStep,
  FrameBuilderStepState,
} from '@frame-builder/lib/types'

import Definitions from './definitions'
import Footer from './footer'
import FrameDimensions from './frame-dimensions'
import PreviewList from './previews'

interface BuilderLayoutRectangle {
  width?: number
  height?: number
}

interface FrameBuilderActionsProps {
  showProgressBar: boolean
  layoutRectangle: BuilderLayoutRectangle
  viewerSize: readonly [number, number] | readonly [null, null]
  isScreenSmallOrLarger?: boolean
  openDimensionDetails: boolean
  getProductImageDataUrl: () => string | undefined
  className?: string
}

const FrameBuilderActions = forwardRef<
  HTMLDivElement,
  FrameBuilderActionsProps
>(
  (
    {
      showProgressBar,
      layoutRectangle,
      viewerSize,
      isScreenSmallOrLarger,
      openDimensionDetails,
      getProductImageDataUrl,
      className,
    },
    ref,
  ) => {
    const { frameBuilderType } = useContext(FrameBuilderSettingsContext)
    const frameBuilderStrings = useContext(FrameBuilderStringsContext)
    const { frameCompletionPercentage } = useContext(
      FrameBuilderValidationContext,
    )

    const containerRef = useRef<HTMLDivElement | null>(null)

    const [openStep, setOpenStep] = useState<FrameBuilderStep | null>(null)
    const [stepStates, setStepStates] =
      useState<Record<FrameBuilderStep, FrameBuilderStepState>>(
        defaultStepStates,
      )

    const configurationContainerSize = useMemo(() => {
      if (
        !isScreenSmallOrLarger ||
        !layoutRectangle.width ||
        !layoutRectangle.height
      ) {
        return [undefined, undefined] as const
      }

      return [undefined, layoutRectangle.width * 0.8] as const
    }, [isScreenSmallOrLarger, layoutRectangle])

    const handleDefinitionClose = useCallback(() => {
      if (openStep === null) {
        return
      }

      setOpenStep(null)
      setStepStates({
        ...defaultStepStates,
        [openStep]: 'closing',
      })
    }, [openStep])

    const handleToggleStep = useCallback(
      (step: FrameBuilderStep, isStepOpen: boolean) => {
        if (!isStepOpen) {
          handleDefinitionClose()
          return
        }

        setOpenStep(step)
        setStepStates({
          ...defaultStepStates,
          [step]: 'opening',
        })

        // Scrolling animation on mobile
        if (containerRef.current && !isScreenSmallOrLarger) {
          const viewerHeight = viewerSize?.[1] ?? 0

          window.scroll({
            // Leave 100px space at the top for overlay header (the 64px is gallery height)
            top: containerRef.current.offsetTop + viewerHeight + 64 - 100,
            behavior: 'smooth',
          })
        }
      },
      [handleDefinitionClose, isScreenSmallOrLarger, viewerSize],
    )

    const handleAnimationFinished = useCallback(() => {
      const newStepStates = getAnimationFinishedStepStates(openStep)
      setStepStates(newStepStates)
    }, [openStep])

    const progressBarWidthPercent = showProgressBar
      ? frameCompletionPercentage
      : 100

    return (
      <div
        ref={ref}
        onTransitionEnd={handleAnimationFinished}
        className={cx(
          'flex flex-col relative w-full sm:w-[260px] md:w-[280px] lg:w-[320px] xl:w-[360px]',
          className,
        )}
        style={{
          height: configurationContainerSize[1],
        }}
      >
        {/* Progress bar */}
        <div className="absolute left-0 top-0 w-full h-[6px] bg-gray-400">
          <div
            className="absolute h-full bg-black transition-all duration-100"
            style={{
              width: `${progressBarWidthPercent}%`,
            }}
          />
        </div>

        <div className="grow-0 shrink flex mt-1.5 overflow-y-auto overflow-x-hidden">
          <PreviewList openStep={openStep} onToggleStep={handleToggleStep} />
          <Definitions
            stepStates={stepStates}
            onClose={handleDefinitionClose}
            className="grow-0 shrink-0"
          />
        </div>

        <Footer
          getProductImageDataUrl={getProductImageDataUrl}
          className="flex-none"
        />

        <FrameDimensions
          title={
            frameBuilderType === 'passepartout'
              ? frameBuilderStrings.passepartoutDimensionsHeading
              : frameBuilderStrings.frameDimensionsHeading
          }
          isOpenByDefault={openDimensionDetails}
          className="mt-6"
        />
      </div>
    )
  },
)

FrameBuilderActions.displayName = 'FrameBuilderActions'

export default FrameBuilderActions
